import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Drawer, Empty, Input, PageHeader, Result, Skeleton, Space, Tag, Tooltip, message,
} from 'antd';
import { AppstoreOutlined, LinkOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';

import BrandingFields from '../../components/business/tenancy/BrandingFields';
import PlanFields from '../../components/business/tenancy/PlanFields';
import PlansDrawer from '../../components/business/tenancy/PlansDrawer';
import usePlans from '../../components/business/tenancy/usePlans';
import TenantCard from '../../components/business/tenancy/TenantCard';
import { showStaffInviteLink, StaffInviteCell } from '../../components/business/tenancy/StaffInvite';
import TenantIdentityFields, { TenantDraft } from '../../components/business/tenancy/TenantIdentityFields';
import RecordTable from '../../components/RecordTable';
import useOperators, { errorMessage, IFirstAdmin } from '../../components/business/tenancy/useOperators';
import {
  ASSET_PRESETS,
  DEFAULT_ASSET_TYPE,
  DEFAULT_THEME_MODE,
  EMPTY_BRANDING,
  IOperator,
  IOperatorBranding,
  IMembership,
  MembershipModel,
  PLATFORM_DEFAULTS,
  assetTypeOf,
} from '../../types/models/tenancy';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .panel {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    padding: 0 24px 24px;
    flex: 1;
    min-height: 0;
  }

  .roster {
    width: 300px;
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-self: stretch;
    overflow-y: auto;
    padding-right: 4px;
  }

  .detail {
    flex: 1;
    min-width: 0;
    align-self: stretch;
    overflow-y: auto;
    border-left: 1px solid var(--ancana-lightergray);
    padding-left: 24px;
  }

  .detail-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
  }

  .detail-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.2;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .detail-sub {
    font-size: 12px;
    color: var(--ancana-font-light);
    margin-top: 2px;
  }

  .columns {
    display: flex;
    gap: 40px;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .column {
    flex: 1;
    min-width: 330px;
  }

  .staff {
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px solid var(--ancana-lightergray);
  }

  .section-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--ancana-font-light);
    margin-bottom: 8px;
  }
`;

/**
 * A new tenant's starting config.
 *
 * The locale/country/timezone defaults match the model's, so a Mexican operator
 * — every tenant so far — is created without touching them. The colors are
 * seeded from the platform defaults rather than left blank: an empty color means
 * "inherit at render time", which is the right STORED value but a confusing
 * thing to show someone on a creation form who is being asked to choose.
 */
const newTenantDraft = (): TenantDraft => ({
  name: '',
  slug: '',
  brand_name: '',
  status: 'active',
  sender_email: '',
  support_email: '',
  portal_base_url: '',
  admin_base_url: '',
  slack_channel: '',
  default_locale: 'es',
  country: 'MX',
  timezone: 'America/Mexico_City',
});

const newBrandingDraft = (): IOperatorBranding => ({
  ...EMPTY_BRANDING,
  theme_tokens: {
    colors: { primary: PLATFORM_DEFAULTS.primary, surface: PLATFORM_DEFAULTS.surface },
    mode: DEFAULT_THEME_MODE,
    radius: PLATFORM_DEFAULTS.radius,
    vocabulary: { assetType: DEFAULT_ASSET_TYPE },
  },
});

const NO_ADMIN: IFirstAdmin = { email: '', first_name: '', last_name: '' };

const FirstAdmin = styled.div`
  margin: 8px 0 24px;

  .section-label {
    font-weight: 600;
    margin-bottom: 8px;
  }
`;

/**
 * The fields a create can't do without. The rest default server-side to a
 * tenant on the shared console (`OperatorViewSet._with_shared_defaults`).
 */
const REQUIRED = ['name', 'slug'] as const;

/** Staff, plus whether each person's invite is still open. */
const StaffModel = {
  ...MembershipModel,
  customTableMappings: { ...MembershipModel.customTableMappings, invite_pending: { omit: true } },
  extraColumns: [{
    key: 'invite',
    title: 'Invite',
    width: 170,
    render: (membership: IMembership) => <StaffInviteCell membership={membership} />,
  }],
};

const toDraft = (operator: IOperator): TenantDraft => {
  const { branding, member_count, created_at, modified_at, ...rest } = operator;
  return rest;
};

/**
 * Tenants — the platform's operator roster and their configuration.
 *
 * A master/detail panel rather than the usual `RecordTable`, because tenants are
 * not a list you page through: there are a handful, they are read side by side,
 * and most of what matters about one is *visual* — its palette, its logo, what
 * it calls its assets. A table of hex strings answers none of that.
 *
 * Staff is the exception and does use the generic `RecordTable`, scoped to the
 * selected tenant: a membership is three plain fields with nothing visual about
 * it, so the standard table — with its search, paging and edit drawer — is
 * exactly right, and gets the office's usual affordances for free.
 *
 * Access is platform staff only, enforced by the API (`IsPlatformStaff`); a
 * non-platform user gets a 403 here and is told so rather than shown an empty
 * roster, which would read as "there are no tenants".
 *
 * Editing is deliberately explicit — a draft plus a Save button, not per-field
 * autosave. These fields repoint a live tenant's sender address and portal URLs,
 * and `theme_tokens` is replaced wholesale on write rather than merged, so a
 * half-applied save is a tenant with a broken portal.
 */
const Tenants = () => {
  const { operators, isLoading, error, reload, onboard, patch } = useOperators();
  const { plans, reload: reloadPlans, save: savePlan, remove: removePlan } = usePlans();
  const [showPlans, setShowPlans] = useState(false);
  const [selectedId, setSelectedId] = useState<number>();
  const [search, setSearch] = useState('');

  const [draft, setDraft] = useState<TenantDraft>({});
  const [brandingDraft, setBrandingDraft] = useState<IOperatorBranding>(EMPTY_BRANDING);
  const [isSaving, setIsSaving] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<TenantDraft>(newTenantDraft);
  const [createBranding, setCreateBranding] = useState<IOperatorBranding>(newBrandingDraft);
  const [createAdmin, setCreateAdmin] = useState<IFirstAdmin>(NO_ADMIN);
  const [isCreating, setIsCreating] = useState(false);

  const selected = operators.find(o => o.id === selectedId);

  // Land on the platform owner (first row) so the panel opens on something.
  useEffect(() => {
    if (!selected && operators.length) setSelectedId(operators[0].id);
  }, [operators, selected]);

  // Reset the draft whenever the selection changes — or when a save returns a
  // fresh row, so server-side normalisation is what stays on screen.
  useEffect(() => {
    if (!selected) return;
    setDraft(toDraft(selected));
    setBrandingDraft(selected.branding || EMPTY_BRANDING);
  }, [selected]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return operators;
    return operators.filter(o => [o.name, o.slug, o.brand_name]
      .some(field => (field || '').toLowerCase().includes(term)));
  }, [operators, search]);

  const isDirty = !!selected && (
    JSON.stringify(draft) !== JSON.stringify(toDraft(selected))
    || JSON.stringify(brandingDraft) !== JSON.stringify(selected.branding || EMPTY_BRANDING)
  );

  const save = async () => {
    if (!selected) return;
    setIsSaving(true);
    try {
      await patch(selected.id, { ...draft, branding: brandingDraft } as Partial<IOperator>);
      message.success(`${draft.brand_name || draft.name} saved.`);
    } catch (e: any) {
      message.error(errorMessage(e, 'Could not save this tenant.'));
    } finally {
      setIsSaving(false);
    }
  };

  // A new tenant starts as a trial of the default trial plan, with its modules.
  const startCreate = () => {
    const plan = plans.find(p => p.is_trial_default) || plans[plans.length - 1];
    if (plan) {
      setCreateDraft(draft => ({
        ...draft,
        plan: plan.id,
        modules: [...plan.modules],
        trial_ends_at: moment().add(plan.trial_days, 'days').format('YYYY-MM-DD'),
      }));
    }
    setCreating(true);
  };

  const missing: string[] = REQUIRED.filter(field => !String(createDraft[field] || '').trim());
  if (!createAdmin.email.trim()) missing.push('first admin email');

  const submitCreate = async () => {
    setIsCreating(true);
    try {
      const { operator, membership, invited } = await onboard(
        { ...createDraft, branding: createBranding } as Partial<IOperator>, createAdmin);
      if (invited) {
        // The email only goes out in production; the link works everywhere.
        showStaffInviteLink(membership.id, `${operator.brand_name} is ready. Send ${createAdmin.email} this link`);
      } else {
        message.success(`${operator.brand_name} created. ${createAdmin.email} already had an account and is now its admin.`, 8);
      }
      setSelectedId(operator.id);
      setCreating(false);
      reloadPlans();  // tenant counts
      setCreateDraft(newTenantDraft());
      setCreateBranding(newBrandingDraft());
      setCreateAdmin(NO_ADMIN);
    } catch (e: any) {
      message.error(errorMessage(e, 'Could not create this tenant.'));
    } finally {
      setIsCreating(false);
    }
  };

  // A 403 is the expected answer for most staff, not a failure to report as one.
  if (error && /403|platform staff/i.test(error)) {
    return (
      <Result
        status="403"
        title="Platform staff only"
        subTitle="Managing tenants needs an active platform_staff membership on the platform-owner operator."
      />
    );
  }

  return (
    <Container>
      <PageHeader
        title="Tenants"
        subTitle={isLoading ? undefined : `${operators.length} operator${operators.length === 1 ? '' : 's'}`}
        extra={[
          <Button key="reload" icon={<ReloadOutlined />} onClick={reload} loading={isLoading} />,
          <Button key="plans" icon={<AppstoreOutlined />} onClick={() => setShowPlans(true)}>
            Plans
          </Button>,
          <Button key="new" type="primary" icon={<PlusOutlined />} onClick={startCreate}>
            New tenant
          </Button>,
        ]}
      />

      {error && !isLoading && (
        <div style={{ padding: '0 24px 16px' }}>
          <Alert type="error" showIcon message={error} />
        </div>
      )}

      <div className="panel">
        <div className="roster">
          <Input.Search
            placeholder="Filter tenants"
            allowClear
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
          {!isLoading && !visible.length && (
            <Empty description={search ? 'No tenant matches' : 'No tenants'} />
          )}
          {visible.map(operator => (
            <TenantCard
              key={operator.id}
              operator={operator}
              selected={operator.id === selectedId}
              onSelect={() => setSelectedId(operator.id)}
            />
          ))}
        </div>

        <div className="detail">
          {!selected && !isLoading && (
            <Empty description="Select a tenant" style={{ marginTop: 64 }} />
          )}
          {selected && (
            <>
              <div className="detail-header">
                <div>
                  <div className="detail-title">
                    {selected.brand_name || selected.name}
                    <Tag>{ASSET_PRESETS[assetTypeOf(selected)].label}</Tag>
                    {selected.is_platform_owner && <Tag color="gold">Platform owner</Tag>}
                  </div>
                  <div className="detail-sub">
                    <span style={{ fontFamily: 'monospace' }}>{selected.slug}</span>
                    {selected.portal_base_url && (
                      <>
                        {' · '}
                        <a href={selected.portal_base_url} target="_blank" rel="noreferrer">
                          <LinkOutlined /> Portal
                        </a>
                      </>
                    )}
                  </div>
                </div>
                <Space>
                  {isDirty && (
                    <Button onClick={() => {
                      setDraft(toDraft(selected));
                      setBrandingDraft(selected.branding || EMPTY_BRANDING);
                    }}>
                      Discard
                    </Button>
                  )}
                  <Tooltip title={isDirty ? undefined : 'No changes'}>
                    <Button type="primary" onClick={save} loading={isSaving} disabled={!isDirty}>
                      Save changes
                    </Button>
                  </Tooltip>
                </Space>
              </div>

              <div className="columns">
                <div className="column">
                  <PlanFields value={draft} onChange={setDraft} plans={plans} saved={selected} />
                  <TenantIdentityFields value={draft} onChange={setDraft} />
                </div>
                <div className="column">
                  <BrandingFields value={brandingDraft} onChange={setBrandingDraft} />
                </div>
              </div>

              <div className="staff">
                <div className="section-label">Staff</div>
                {/* Keyed on the tenant so the table refetches and resets its own
                    paging when the selection changes, rather than showing the
                    previous tenant's roster while the new one loads. */}
                <RecordTable<IMembership>
                  key={selected.id}
                  embedded
                  size="small"
                  recordModel={StaffModel}
                  recordsetFilters={{ operator: selected.id }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <Drawer
        title="New tenant"
        width={720}
        visible={creating}
        onClose={() => setCreating(false)}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Tooltip title={missing.length ? `Still needed: ${missing.join(', ')}` : undefined}>
              <Button
                type="primary"
                onClick={submitCreate}
                loading={isCreating}
                disabled={!!missing.length}
              >
                Create tenant
              </Button>
            </Tooltip>
          </div>
        )}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
          message="Creating a tenant does not move any data"
          description="It creates the company's account on the shared Caim console, seeds its defaults (a usage pattern and disabled comms rules), and makes the person below its admin. They get an email (production only) and you get a link to share, either of which lets them set a password and land on their setup checklist. Only a name, a slug and the admin's email are needed."
        />
        <TenantIdentityFields value={createDraft} onChange={setCreateDraft} isNew />
        <PlanFields value={createDraft} onChange={setCreateDraft} plans={plans} />
        <FirstAdmin>
          <div className="section-label">First admin</div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder="Email"
              type="email"
              value={createAdmin.email}
              onChange={e => setCreateAdmin({ ...createAdmin, email: e.target.value })}
            />
            <Space style={{ width: '100%' }}>
              <Input
                placeholder="First name"
                value={createAdmin.first_name}
                onChange={e => setCreateAdmin({ ...createAdmin, first_name: e.target.value })}
              />
              <Input
                placeholder="Last name"
                value={createAdmin.last_name}
                onChange={e => setCreateAdmin({ ...createAdmin, last_name: e.target.value })}
              />
            </Space>
          </Space>
        </FirstAdmin>
        <BrandingFields value={createBranding} onChange={setCreateBranding} />
      </Drawer>
      <PlansDrawer
        visible={showPlans}
        onClose={() => setShowPlans(false)}
        plans={plans}
        save={savePlan}
        remove={removePlan}
      />
    </Container>
  );
};

export default Tenants;
